import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import { getERinstruction, saveERinstruction } from "../../services/erinstructions";
import { getUsers } from "../../services/users";
import ActionCard from "../../components/kanban/ActionCard";
import auth from "../../services/authservice";
import { connect } from 'react-redux';
import { getCurrentUser, loadCurrentUser } from '../../store/users';
import "./ERInstructionBoard.css";
import CustomQRCode from "../../components/common/CustomQRCode";
import logo from "../../assets/images/logo.png";

const ERInstructionBoard = ({ currentUser, loadCurrentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [erInstruction, setERInstruction] = useState(null);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  

  // Helper method to get businessNo from user's accountNo if Company role
  const getBusinessNoFromUser = () => {
    const isCompanyRole = (currentUser?.roleName === "Company") || 
                         (currentUser?.role === "Company") ||
                         (currentUser?.role?.name === "Company");
    
    if (currentUser && isCompanyRole && currentUser?.accountNo) {
      return currentUser.accountNo;
    }
    return '';
  };

  // Helper method to create updated er instruction object (eliminates code duplication)
  const createUpdatedERInstruction = (actionsArray) => {
    const updatedERInstruction = {
      _id: erInstruction._id,
      user: typeof erInstruction.user === 'string' ? erInstruction.user : (erInstruction.user?._id || auth.getProfile()._id),
      name: erInstruction.name || '',
      description: erInstruction.description || '',
      category: erInstruction.category || '',	  
      fieldType: erInstruction.fieldType || '',	  
      serviceType: erInstruction.serviceType || '',	  
      tags: erInstruction.tags || '',
      copyright: erInstruction.copyright || '',      
      version: erInstruction.version || '',
      language: erInstruction.language || '',
      reference: erInstruction.reference || '',
      internalVideoLink: erInstruction.internalVideoLink || '',
      actions: actionsArray,
      note: erInstruction.note || '',
      status: erInstruction.status || 'active',
      createdOn: erInstruction.createdOn, // Keep original creation date
      editedOn: new Date() // Only update the edited date
    };

    // Set businessNo
    if (erInstruction.businessNo) {
      updatedERInstruction.businessNo = typeof erInstruction.businessNo === 'object' 
        ? erInstruction.businessNo._id 
        : erInstruction.businessNo;
    } else {
      const businessNo = getBusinessNoFromUser();
      if (businessNo) {
        updatedERInstruction.businessNo = businessNo;
      }
    }

    return updatedERInstruction;
  };

  useEffect(() => {
    const userProfile = auth.getProfile();
    if (userProfile?._id) {
      loadCurrentUser(userProfile._id);
    }
    
    fetchData();
  }, [id, loadCurrentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [wiResponse, usersResponse] = await Promise.all([
        getERinstruction(id),
        getUsers(),
      ]);

      setERInstruction(wiResponse.data);
      setUsers(usersResponse.data);
      
      // Use embedded actions from er instruction
      let erInstructionActions = [];
      if (wiResponse.data.actions && Array.isArray(wiResponse.data.actions)) {
        erInstructionActions = wiResponse.data.actions.map(
          (embeddedAction, index) => ({
            _id: `action_${index}`, 
            name: embeddedAction.name || "",
            step: embeddedAction.step || (index + 1).toString(),
            assignee: embeddedAction.assignee || [],
            assigneeType: embeddedAction.assigneeType || [],
          })
        );
      }

      const sortedActions = erInstructionActions.sort((a, b) => {
        const stepA = parseInt(a.step) || 0;
        const stepB = parseInt(b.step) || 0;
        return stepA - stepB;
      });

      setActions(sortedActions);
    } catch (err) {
      setError("Failed to load er instruction board");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    if (source.index === destination.index) return;

    const newActions = Array.from(actions);
    const [reorderedAction] = newActions.splice(source.index, 1);
    newActions.splice(destination.index, 0, reorderedAction);

    // Update step numbers
    const updatedActions = newActions.map((action, index) => ({
      ...action,
      step: (index + 1).toString()
    }));

    setActions(updatedActions);

    try {
      // Convert back to embedded format and save to er instruction
      const embeddedActions = updatedActions.map((action) => ({
        name: action.name || "",
        step: action.step || "",
        assignee: action.assignee || [],
        assigneeType: action.assigneeType || [],
      }));

      const updatedERInstruction = createUpdatedERInstruction(embeddedActions);

      await saveERinstruction(updatedERInstruction);
      // Refresh data to ensure consistency
      await fetchData();
    } catch (error) {
      // Revert on error
      await fetchData();
    }
  };

  const createNewAction = async () => {
    const newEmbeddedAction = {
      name: "New Action",
      step: (actions.length + 1).toString(),
      assignee: [],
            assigneeType: [],
    };

    try {
      const updatedActions = [...(erInstruction.actions || []), newEmbeddedAction];
      
      // Clean actions
      const cleanActions = updatedActions.map((action) => ({
        name: action.name || "",
        step: action.step || "",
        assignee: action.assignee || [],
        assigneeType: action.assigneeType || [],
      }));

      const updatedERInstruction = createUpdatedERInstruction(cleanActions);

      await saveERinstruction(updatedERInstruction);
      await fetchData();
    } catch (error) {
      setError("Failed to create new action: " + (error.response?.data || error.message || error));
    }
  };
  const handleActionUpdate = async (updatedAction) => {
    try {
      // Find the index of the action being updated
      const actionIndex = actions.findIndex(action => action._id === updatedAction._id);
      if (actionIndex === -1) {
        throw new Error("Action not found");
      }

      // Update the embedded actions in er instruction
      const updatedEmbeddedActions = erInstruction.actions.map(
        (embeddedAction, index) => {
          if (index === actionIndex) {
            return {
              name: updatedAction.name || "",
              step: updatedAction.step || (index + 1).toString(),
              assignee: updatedAction.assignee || [],
              assigneeType: updatedAction.assigneeType || [],
            };
          }
          return {
            name: embeddedAction.name || "",
            step: embeddedAction.step || "",
            assignee: embeddedAction.assignee || [],
            assigneeType: embeddedAction.assigneeType || [],
          };
        }
      );

      const updatedERInstruction = createUpdatedERInstruction(updatedEmbeddedActions);

      await saveERinstruction(updatedERInstruction);
      await fetchData();
    } catch (error) {
      setError("Failed to update action: " + (error.response?.data || error.message || error));
    }
  };
  const handleActionDelete = async (actionId) => {
    try {
      // Find the index of the action to delete
      const actionIndex = actions.findIndex(action => action._id === actionId);
      if (actionIndex === -1) {
        throw new Error("Action not found");
      }

      // Remove the action from embedded actions array
      const updatedEmbeddedActions = erInstruction.actions.filter((_, index) => index !== actionIndex);
      
      // Renumber the steps
      const renumberedActions = updatedEmbeddedActions.map((action, index) => ({
        name: action.name || "",
        step: (index + 1).toString(),
        assignee: action.assignee || [],
        assigneeType: action.assigneeType||[],
      }));

      const updatedERInstruction = createUpdatedERInstruction(renumberedActions);

      await saveERinstruction(updatedERInstruction);
      await fetchData();
    } catch (error) {
      setError("Failed to delete action: " + (error.response?.data || error.message || error));
    }
  };

  const handleGoBack = () => {
    navigate("/erinstructions/erinstructions");
  };

  if (loading) {
    return (
      <div className="board-loading">
        <div className="loading-spinner"></div>
        <p>Loading er instruction board...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="board-error">
        <p>{error}</p>
        <button onClick={handleGoBack} className="back-button">
          Back to ER Instructions
        </button>
      </div>
    );
  }

  if (!erInstruction) {
    return (
      <div className="board-error">
        <p>ER instruction not found</p>
        <button onClick={handleGoBack} className="back-button">
          Back to ER Instructions
        </button>
      </div>
    );
  }

  return (
    <div className="er-instruction-board">
      <div
        className="board-header"
        style={{display:"flex", justifyContent:"space-between"}}
      >
        <div>
          <button onClick={handleGoBack} className="back-button">
            ← Back to ER Instructions
          </button>
          <h1 className="board-title">{erInstruction.name}</h1>
          {erInstruction.description && (
            <p className="board-description">{erInstruction.description}</p>
          )}
        </div>

        <CustomQRCode logo={logo} value={id} />
      </div>

      <div className="board-content">
        {/* Left Column - Action Steps */}
        <div className="action-steps">
          <Panel>
            <PanelHeader>
              <div className="panel-header-content">
                <span>Action Steps</span>
                <div>
                  <span className="action-count">{actions.length} actions</span>
                  <button 
                    onClick={createNewAction}
                    className="add-action-btn"
                    title="Add New Action"
                  >
                    + Add Action
                  </button>
                </div>
              </div>
            </PanelHeader>
            <PanelBody>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="actions-list">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`actions-container ${
                        snapshot.isDraggingOver ? "dragging-over" : ""
                      }`}
                    >
                      {actions.length === 0 ? (
                        <div className="empty-state">
                          <p>No actions found for this er instruction</p>
                          <p>Use the "Add Action" button above to create new actions</p>
                        </div>
                      ) : (
                        actions.map((action, index) => (
                          <ActionCard
                            key={action._id}
                            action={action}
                            index={index}
                            onUpdate={handleActionUpdate}
                            onDelete={handleActionDelete}
                            users={users}
                          />
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </PanelBody>
          </Panel>
        </div>

        {/* Right Column - ER Instruction Details */}
        <div className="er-instruction-details">
          <Panel>
            <PanelHeader>
              <div className="panel-header-content">
                <span>ER Instruction Details</span>
              </div>
            </PanelHeader>
            <PanelBody>
              <div className="details-container">
                <div className="detail-group">
                  <h3 className="detail-title">About</h3>
                  <div className="detail-item">
                    <strong>Name:</strong> 
                    <span>{erInstruction.name}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Description:</strong> 
                    <span>{erInstruction.description || 'No description'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>FieldType:</strong> 
                    <span>{erInstruction.fieldType || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Category:</strong> 
                    <span>{erInstruction.category || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>ServiceType:</strong> 
                    <span>{erInstruction.serviceType || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Version:</strong> 
                    <span>{erInstruction.version || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Language:</strong> 
                    <span>{erInstruction.language || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Tags:</strong> 
                    <span>{erInstruction.tags || 'N/A'}</span>
                  </div>
					<div className="detail-item">
					  <strong>VideoLink:</strong>
					  {erInstruction.internalVideoLink ? (
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						  <span>{erInstruction.internalVideoLink}</span>
						  <button 
							onClick={() => setIsPlaying(!isPlaying)}
							className="play-button"
						  >
							{isPlaying ? "Hide Video" : "▶ Play"}
						  </button>
						</div>
					  ) : (
						<span>N/A</span>
					  )}
					</div>
					{isPlaying && erInstruction.internalVideoLink && (
					  <div className="video-player-container">
						<video 
						  controls 
						  width="100%" 
						  style={{ marginTop: "10px", borderRadius: "8px" }}
						>
						  <source src={erInstruction.internalVideoLink} type="video/mp4" />
						  Your browser does not support the video tag.
						</video>
					  </div>
					)}
                  <div className="detail-item">
                    <strong>Reference:</strong> 
                    <span>{erInstruction.reference || 'N/A'}</span>
                  </div>
                  
                </div>

                <div className="detail-group">
                  <h3 className="detail-title">Owner Information</h3>
                  {erInstruction.user ? (
                    <div className="owner-section">
                      <div className="owner-info">
                        <div className="owner-avatars">
                          {/* User Avatar */}
                          {erInstruction.user.imageSrc ? (
                            <img 
                              src={erInstruction.user.imageSrc} 
                              alt="User Avatar"
                              className="owner-avatar-image"
                            />
                          ) : (
                            <div className="owner-avatar-placeholder">
                              <i className="fa fa-user"></i>
                            </div>
                          )}
                        </div>
                        <div className="owner-details">
                          <div className="owner-name">
                            {erInstruction.user.contactName?.first} {erInstruction.user.contactName?.last}
                          </div>
                          <div className="owner-email">
                            {erInstruction.user.email || 'No email'}
                          </div>
                          <div className="owner-username">
                            @{erInstruction.user.username || 'N/A'}
                          </div>
  
                          
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-owner">No owner assigned</div>
                  )}
                </div>

                <div className="detail-group">
                  <h3 className="detail-title">Business Information</h3>
                  {erInstruction.businessNo ? (
                    <div className="business-info">
                      <div className="business-section">
                        <div className="business-avatar">
                          {(() => {
                            const businessLogo = erInstruction?.companyinfo?.companies?.imageSrc || 
                                               erInstruction?.companyinfo?.imageSrc || 
                                               erInstruction?.businessNo?.companyInfo?.businessLogo || 
                                               erInstruction?.businessNo?.businessLogo || 
                                               erInstruction?.businessNo?.logo || 
                                               erInstruction?.businessNo?.companyInfo?.logo ||
                                               erInstruction?.businessNo?.image ||
                                               erInstruction?.businessNo?.companyInfo?.image;
                            
                            const finalLogo = businessLogo || (erInstruction?.user?.imageSrc);
                            
                            return finalLogo ? (
                              <img 
                                src={finalLogo} 
                                alt="Business Logo"
                                className="business-logo-image"
                                style={{ width: "20px", height: "20px", borderRadius: "50%", marginRight: "4px" }}
                              />
                            ) : (
                              <div className="business-logo-placeholder" style={{ 
                                width: "20px", 
                                height: "20px", 
                                borderRadius: "50%", 
                                backgroundColor: "#28a745",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "4px",
                                fontSize: "10px",
                                color: "white"
                              }}>
                                <i className="fa fa-building"></i>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="business-details">
                          <div className="business-name">
                            {erInstruction?.businessNo?.companyInfo?.businessName || 
                             erInstruction?.businessNo?.name || 
                             'No Business'}
                          </div>
                          <div className="business-type">
                            Business Entity
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-business">No business assigned</div>
                  )}
                </div>

                <div className="detail-group">
                  <h3 className="detail-title">Additional Information</h3>
                  <div className="detail-item">
                    <strong>Created On:</strong> 
                    <span>{erInstruction.createdOn ? (() => {
                      const date = new Date(erInstruction.createdOn);
                      const formattedDate = date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      });
                      const formattedTime = date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      });
                      return `${formattedDate} ${formattedTime}`;
                    })() : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Edited On:</strong> 
                    <span>{erInstruction.editedOn ? (() => {
                      const date = new Date(erInstruction.editedOn);
                      const formattedDate = date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      });
                      const formattedTime = date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      });
                      return `${formattedDate} ${formattedTime}`;
                    })() : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Notes:</strong> 
                    <span>{erInstruction.note || 'No notes'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Total Actions:</strong> 
                    <span>{actions.length}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Copyrights:</strong> 
                    <span>{erInstruction.copyright || ''}</span>
                  </div>                  
                </div>
              </div>
            </PanelBody>
          </Panel>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentUser: getCurrentUser(state)
});

const mapDispatchToProps = (dispatch) => ({
  loadCurrentUser: (id) => dispatch(loadCurrentUser(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(ERInstructionBoard);
